import { Chains, ServiceIcon, statusText, TokenPair, Trader } from './TradeListColumn';
import GridPagination from './GridPagination';
import { Box, CircularProgress, Theme, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Success from '../icons/Success';
import Link from 'next/link';

type TradeListProps = {
	data?: TradeListItemResponse[];
	loading: boolean;
	count: number;
	page: number;
	empty?: JSX.Element;
	onPageChange: (newPage: number) => void;
};

function Row({
	item,
}: { item: NonNullable<TradeListProps['data']>[number] }) {
	const timeString = useMemo<string>(() => moment(item.initiatedAt).fromNow(), [item.initiatedAt]);
	const statusName = useMemo(() => statusText(item), [item]);
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	return (
		<Link
			href={`/tx/${item.orderId}`}
			className={classNames(
				'flex flex-col items-start mobile:flex-col md:grid md:items-center gap-4 md:gap-6',
				'p-6',
				'transition bg-backgroundPrimary hover:bg-backgroundSecondary',
				'cursor-pointer',
			)}
			style={{ gridTemplateColumns: '170px 1fr 2fr 100px 1fr' }}
		>
			<div className='flex items-center gap-1'>
				<ServiceIcon className="text-sm" service={item.service} />
				<Typography variant='appSmallRegular'>{timeString}</Typography>
			</div>
			<Box><Trader address={item.trader} nested={true} /></Box>
			<TokenPair row={item} />
			<Chains
				chain1={item.sourceChain}
				chain2={item.service === 'WH_BRIDGE' ? item.sourceChain : item.swapChain}
				chain3={item.destChain}
				small={isMobile}
			/>
			<Typography variant='appSmallBold' className={classNames(isMobile ? '' : 'ml-6')} color="common.textPrimary" sx={{ background: (theme) => item.isCompleted ? theme.palette.common.colorfulBorder : theme.palette.common.textPrimary, backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
				{statusName}
				{item?.isCompleted && (
					<Success sx={{ fontSize: '1.75em', ml: '0.25em' }} useGradient />
				)}
			</Typography>
		</Link>
	);
}

export default function TradeList({ data, count, page, loading, onPageChange, empty }: TradeListProps) {
	const [parent] = useAutoAnimate();
	const [currentData, setData] = useState(data);

	useEffect(() => {
		if (!loading) {
			setData(data);
		}
	}, [data, loading]);

	return (
		<div className='flex flex-col gap-6'>
			<div
				ref={parent}
				className={classNames('rounded-md border border-borderPrimary', 'divide-y divide-borderPrimary', 'flex flex-col')}
			>
				{currentData ? (
					currentData.length > 0 ? (
						currentData.map((item) => <Row key={item.id} item={item} />)
					) : (
						(empty ?? null)
					)
				) : (
					<div className='flex items-center justify-center min-h-48'>
						<CircularProgress />
					</div>
				)}
			</div>
			{currentData && <GridPagination loading={loading} page={page} onChange={onPageChange} pageCount={count} />}
		</div>
	);
}
